import './AllProjects.scss'
import React, { useState, useContext, useEffect, useRef, useMemo, useCallback } from 'react'
import { ProjectsContext } from '../../utils/ProjectsContext'
import { Link } from 'react-router-dom'
import DOMPurify from 'dompurify'
import MainPhoto from '../../components/MainPhoto/MainPhoto'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faAnglesDown } from '@fortawesome/free-solid-svg-icons'
import { getMediaUrl } from '../../utils/getMediaUrl'

function AllProjects() {
    const {
        projects,
        projectCategories,
        setDisplayHeader,
        displayHeader
    } = useContext(ProjectsContext)

    const [categoryProjects, setCategoryProjects] = useState('tous')
    const filtersAnchorRef = useRef(null)

    const scrollWithOffset = useCallback((element, offset = 100) => {
        if (!element) return

        const y = element.getBoundingClientRect().top + window.scrollY - offset

        window.scrollTo({
            top: y,
            behavior: 'smooth'
        })
    }, [])

    const handleScrollToFilters = useCallback(() => {
        scrollWithOffset(filtersAnchorRef.current, 100)
    }, [scrollWithOffset])

    const handleCategoryClick = useCallback((category) => {
        setCategoryProjects(category)
        scrollWithOffset(filtersAnchorRef.current, 100)
    }, [scrollWithOffset])

    const getMediaType = useCallback((media) => {
        const fileType = media?.fileType || ''

        if (fileType.startsWith('video/')) return 'video'
        if (fileType.startsWith('image/')) return 'image'
        if (media?.imageUrl) return 'image' // legacy fallback

        return null
    }, [])

    


    const renderProjectMedia = useCallback((media, alt = '') => {
        if (!media?.imageUrl) return null

        const mediaType = getMediaType(media)
        
        // if (mediaType === 'video') {
        //     return (
        //         <video
        //             src={media.imageUrl}
        //             muted
        //             playsInline
        //             autoPlay
        //             loop
        //             preload="metadata"
        //         />
        //     )
        // }

        // if (mediaType === 'image') {
        //     return (
        //         <img
        //             src={media.imageUrl}
        //             alt={alt}
        //             loading="lazy"
        //             decoding="async"
        //         />
        //     )
        // }
        if (mediaType === 'video') {
            return (
                <video
                    src={getMediaUrl(media.imageUrl)}
                    muted
                    playsInline
                    autoPlay
                    loop
                    preload="metadata"
                />
            )
        }
        
        if (mediaType === 'image') {
            return (
                <img
                    src={getMediaUrl(media.imageUrl)}
                    alt={alt}
                    loading="lazy"
                    decoding="async"
                />
            )
        }

        return null
    }, [getMediaType])

    useEffect(() => {
        window.scrollTo(0, 0)
    }, [])

    const availableCategories = useMemo(() => {
        return projectCategories.filter(category =>
            projects.some(project => project.projectType === category)
        )
    }, [projectCategories, projects])

    const sortedProjects = useMemo(() => {
        const filteredProjects =
            categoryProjects === 'tous'
                ? projects
                : projects.filter(project => project.projectType === categoryProjects)

        return [...filteredProjects]
            .map(project => {
                const [year = '0', month = '0'] = (project.creationDate || '').split('-')

                return {
                    ...project,
                    numericDate: parseInt(`${year}${month}`, 10) || 0
                }
            })
            .sort((a, b) => b.numericDate - a.numericDate)
    }, [projects, categoryProjects])


    


    return (
        <main
            className='allProjects'
            onClick={() => displayHeader === true && setDisplayHeader(false)}
        >
            <div className='allProjects_topContainer'>
                <MainPhoto />

                <button
                    className='allProjects_buttonsContainer_button--anchor'
                    type="button"
                    aria-label="Aller aux filtres projets"
                    onClick={handleScrollToFilters}
                >
                    <FontAwesomeIcon icon={faAnglesDown} />
                </button>
            </div>

            <ul
                className='allProjects_buttonsContainer'
                id='projects-filters'
                ref={filtersAnchorRef}
            >
                <li className='allProjects_buttonsContainer_button'>
                    <button
                        className={
                            categoryProjects === 'tous'
                                ? 'allProjects_buttonsContainer_button--selected'
                                : 'allProjects_buttonsContainer_button--notSelected'
                        }
                        type='button'
                        aria-pressed={categoryProjects === 'tous'}
                        aria-label='Afficher toutes les catégories de projets'
                        onClick={() => handleCategoryClick('tous')}
                    >
                        <h3>Tous</h3>
                    </button>
                </li>

                {availableCategories.map(category => (
                    <li key={category} className='allProjects_buttonsContainer_button'>
                        <button
                            className={
                                categoryProjects === category
                                    ? 'allProjects_buttonsContainer_button--selected'
                                    : 'allProjects_buttonsContainer_button--notSelected'
                            }
                            type='button'
                            aria-pressed={categoryProjects === category}
                            aria-label={`Afficher les projets correspondants à la catégorie ${category}`}
                            onClick={() => handleCategoryClick(category)}
                        >
                            <h3 className='allProjects_buttonsContainer_button_h3'>
                                {category}
                            </h3>
                        </button>
                    </li>
                ))}
            </ul>

            <ul className='allProjects_projectsList'>
                {sortedProjects.map((project, index) => {
                    const mainMedia = project.projectImages?.[project.mainImageIndex]
                    const projectUrl = `/projets/${project.slug || project._id}`
                    const projectYear = project.creationDate?.split('-')[0]

                    return (
                        <li key={project._id}>
                            <Link
                                to={projectUrl}
                                className='allProjects_projectCard'
                                aria-label={`Accéder à la page du projet ${project.title}`}
                            >
                                <div className='allProjects_projectCard_infos'>
                                    <h4
                                        translate='no'
                                        className='allProjects_projectCard_infos_title'
                                    >
                                        {project.title}
                                    </h4>

                                    <p
                                        translate='no'
                                        className='allProjects_projectCard_infos_subtitle'
                                    >
                                        {project.subtitle}
                                    </p>

                                    <p className='allProjects_projectCard_infos_projectInfos'>
                                        {project.projectInfos}
                                    </p>

                                    <p className='allProjects_projectCard_infos_creationDate'>
                                        {projectYear}
                                    </p>

                                    {project.summary && (
                                        <p
                                            className='allProjects_projectCard_infos_summary'
                                            dangerouslySetInnerHTML={{
                                                __html: DOMPurify.sanitize(project.summary)
                                            }}
                                        />
                                    )}
                                </div>

                                <figure className='allProjects_projectCard_imageContainer'>
                                    {renderProjectMedia(
                                        mainMedia,
                                        `image principale du projet ${project.title} (${index + 1})`
                                    )}
                                </figure>
                            </Link>
                        </li>
                    )
                })}
            </ul>
        </main>
    )
}

export default AllProjects